import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import { saveAs } from 'file-saver';
import { BlobReader, Entry, ZipReader } from '@zip.js/zip.js';
import _, {
  camelCase,
  cloneDeep,
  find,
  flatten,
  omit,
  snakeCase,
} from 'lodash';
import {
  attachNote as attachNoteApi,
  createCluster,
  createShape as createShapeApi,
  deleteCluster as deleteClusterApi,
  deleteFloorCalibration,
  deleteNote as deleteNoteApi,
  deleteShape as deleteShapeApi,
  deleteSpace as deleteSpaceApi,
  fetchFloorplanCalibrationApi,
  fetchShapes as fetchShapesApi,
  fetchSpacesWorksheetSpreadsheet as fetchSpacesWorksheetSpreadsheetApi,
  FloorPlan as floorPlanApi,
  getClusters,
  getSpace as getSpaceApi,
  IShapeResponse,
  Space as spaceApi,
  updateFloorplan as updateFloorplanApi,
  updateNote as updateNoteApi,
  updateShape as updateShapeApi,
  uploadFloorplanPDF,
} from 'spw-api';
import { createSpaceFromClusterApi, updateClusterApi } from 'spw-api/clusters';
import { IAppState } from 'spw-redux/reducer';
import { APIResponse, IMap } from 'spw-redux/types';
import { isNil, set, view } from 'ramda';
import {
  getProjectInfoById,
  refreshCurrentProject,
  setCurrentBuilding,
  setCurrentFloor,
  updateBuildingFloor,
} from 'spw-redux/modules/projects/actions';
import { isUSFMeasurementState } from 'spw-components/floorplan/calibration-constants';
import {
  AMENITY_SPACES_CATEGORY,
  AMENITY_SPACES_KEY,
  CUSTOM_CATEGORY,
  CUSTOM_SUBCATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  MEETING_SPACES_KEY,
  PRIVATE_OFFICES_SUBCATEGORY,
  REQUEST_STATUS,
  SUPPORT_SPACES_CATEGORY,
  SUPPORT_SPACES_KEY,
  WORKSTATIONS_SUBCATEGORY,
  XLSX_MIME_TYPE,
} from 'spw-constants';
import {
  getAreaOfShape,
  isEmptyShape,
  latLngsToMultiPolygonPoints,
} from 'spw-components/floorplan/geometry-utils';
import { shouldOpenConfirmExitFloorplanOperationPrompt } from 'spw-components/floorplan/floor-plan-map-utils';
import {
  handleError,
  showToastMessage,
} from 'spw-redux/modules/UI/messages/actions';
import * as Sentry from '@sentry/react';
import { ISpaceTypeCategory } from 'spw-redux/modules/space_types/types';
import { INote } from 'spw-redux/modules/notes/types';
import { FLOORPLAN_UPLOAD_STATUS } from 'spw-components/floorplan/poll-floorplan-upload-status-hook';
import {
  buildSpacesMap,
  compressEditHistory,
  otherSpaceCategoryPath,
  spaceCategoryPath,
} from 'spw-redux/modules/floorplan/utils';
import { selectAreaUnit } from 'spw-components/unit-conversion/selectors';
import { getFloorSpacesApi } from 'spw-api/space';
import {
  areCalibrationLinesEqual,
  isSuggestedCalibrationValid,
} from 'spw-esa-projects/building-details/floorplan/calibration/utils';
import {
  actions,
  ADD_SPACE,
  ADD_UNATTACHED_NOTE,
  addItemToEditHistory,
  addSpace as addSpaceAction,
  ARCHIVE_FLOORPLAN,
  archiveFloorplan,
  ATTACH_NOTE,
  attachNoteSucceeded as attachNoteSucceededAction,
  CANCEL_PDF_UPLOAD,
  cleanEditHistory,
  CLEAR_SELECTED_SPACE,
  CONFIRM_ADD_SPACE,
  confirmAddSpace as confirmAddSpaceAction,
  COPY_MEASURED_FLOOR_USF,
  CREATE_SPACE_FROM_CLUSTER,
  CREATE_SPACES,
  createSpaceFromCluster,
  createSpaces as createSpacesAction,
  CROP_FLOORPLAN_IMAGE,
  DELETE_ACTIVE_SHAPE,
  DELETE_CLUSTER,
  DELETE_NOTE,
  DELETE_SELECTED_SPACES,
  DELETE_SHAPE,
  DELETE_SPACE,
  deleteCluster as deleteClusterAction,
  deleteClusterFailed,
  deleteClusterSucceded,
  deleteShape as deleteShapeAction,
  enableAllViewLayers,
  EXIT_FLOORPLAN_OPERATION,
  EXIT_FLOORPLAN_OPERATION_CANCEL,
  EXIT_FLOORPLAN_OPERATION_CONFIRM,
  exitFloorplanOperation as exitFloorplanOperationAction,
  exitFloorplanOperationCancel,
  exitFloorplanOperationConfirm,
  FETCH_CALIBRATION,
  FETCH_FLOORPLAN,
  FETCH_FLOORPLAN_GEOJSON,
  FETCH_FLOORPLAN_PDF,
  FETCH_FLOORPLAN_SHAPES,
  FETCH_FLOORPLAN_UPLOAD,
  FETCH_SPACES_WORKSHEET_SPREADSHEET,
  FETCH_SUGGESTED_CALIBRATION,
  fetchCalibration as fetchCalibrationAction,
  fetchCalibrationSucceeded,
  fetchFloorplanFailed,
  fetchFloorplanPDFFailed,
  fetchFloorplanPDFSuccess,
  fetchFloorplanShapes as fetchShapesAction,
  fetchFloorplanSucceeded,
  fetchSpacesWorksheetSpreadsheet as fetchSpacesWorksheetSpreadsheetAction,
  fetchSpacesWorksheetSpreadsheetFailed,
  fetchSpacesWorksheetSpreadsheetSuccess,
  fetchSuggestedCalibration,
  fetchSuggestedCalibrationSucceeded,
  GET_SPACE_TO_UPDATE,
  GET_SPACES,
  getFloorSpacesSucceeded,
  getSpaces as getSpacesAction,
  getSpaces,
  getSpacesSucceeded,
  NOTE_FILTER_CLICKED,
  noteFilterClicked,
  PHOTO_FILTER_CLICKED,
  photoFilterClicked,
  populateCalibration,
  populateClusters,
  populateFloorplanShapes,
  PUT_DISTANCE_CONSTANT,
  restoreControlsState as restoreControlsStateAction,
  SAVE_ACTIVE_SHAPE,
  SAVE_FLOOR_IMAGE,
  SEAT_COUNT_CLICKED,
  SET_ADD_FLOOR_NOTE_STATE,
  SET_BULK_SELECTION_STATE,
  setActiveShape,
  setBulkOperationSourceFloorData,
  setBulkSelectionState,
  setControlsState as setControlsStateAction,
  setControlStateValue,
  setCurrentFloorplanUpload,
  setDidUpdateFloorPlan,
  setFloorImage as setFloorImageAction,
  setFloormapState as setFloormapStateAction,
  setFloorplanGeoJson,
  setFloorplanPreviewState,
  setIsArchivingFloorplan,
  setIsMeasuredShapeDirty,
  setIsUploadingPDF,
  setMeasurementState,
  setOriginalFileDataUrl,
  setOriginalImageDimensions,
  setSelectedSpace,
  setSelectedSpaceLayers,
  setSpaceToUpdate,
  setSuggestedCalibration,
  showSeatCountLayer,
  showSpaceTypeLayer,
  SPACE_TYPE_CLICKED,
  START_PDF_UPLOAD,
  startPDFUpload as startPDFUploadAction,
  TOGGLE_AMENITY_SPACES,
  TOGGLE_INDIVIDUAL_SPACES,
  TOGGLE_MEETING_SPACES,
  TOGGLE_SUPPORT_SPACES,
  toggleAmenitySpaces,
  toggleIndividualSpaces,
  toggleIndividualSpacesSuccess,
  toggleMeetingSpaces,
  toggleOnlyWithNote,
  toggleOnlyWithPhoto,
  toggleSupportSpaces,
  toggleWorkspaceIntermediate,
  UPDATE_ACTIVE_SHAPE,
  UPDATE_CLUSTER,
  UPDATE_MULTIPLE_SPACES,
  UPDATE_NOTE,
  UPDATE_SPACE,
  UPDATE_SPACE_BUBBLE_POSITION,
  UPDATE_SPACE_POSITION,
  UPDATE_SUGGESTED_CALIBRATION,
  updateMultipleSpaces,
  updateShape as updateShapeAction,
  updateSpace as updateSpaceAction,
  updateSpaceBubblePosition,
  updateSpacePosition as updateSpacePositionAction,
  UPLOAD_FLOORPLAN,
  uploadFloorplan as uploadFloorplanAction,
  uploadFloorplanFailed,
  uploadFloorplanSucceeded,
  VIEW_SPACES_USF_CLICKED,
} from './actions';
import {
  camelize,
  convertBase64StringToBlob,
  deleteAtIndex,
  removeTypeNameFromObject,
  replaceAtIndex,
  typeCheck,
  unPackPolygonsMatrixForPersistence,
} from '../../../shared/utils';
import {
  BulkSelectionState,
  EditAction,
  EditHistory,
  FloorMapStates,
  IAdjustedFloorplanItems,
  ICalibration,
  ICluster,
  IFloorplan,
  IFloorplanControlsState,
  IMinimalSpace,
  ISpace,
  ISpaceShape,
  MeasurementState,
  SEAT_COUNT_ENABLED,
  SPACE_TYPE_ENABLED,
  SpaceShapeVM,
  SuggestedCalibration,
  VIEW_SPACES_USF_ENABLED,
} from './types';

import {
  selectActiveShape,
  selectAdjustedFloorplanItems,
  selectAllShapes,
  selectBulkOperationSourceFloorCalibration,
  selectBulkSelectionState,
  selectCalibration,
  selectClusters,
  selectCurrentControlState,
  selectCurrentFloorplan,
  selectEditHistory,
  selectFloorMapState,
  selectFloorplanPreviewImageBounds,
  selectFloorplanRequestStatus,
  selectFloorSpaces,
  selectFloorUsfShapeToCopy,
  selectIsUploadingFloorplan,
  selectMeasurementState,
  selectOriginalFileDataUrl,
  selectOriginalImageDimensions,
  selectSelectedSpaces,
  selectSpacesMap,
  selectSpaceToUpdate as spaceToUpdateSelector,
  selectSuggestedCalibration,
} from './selectors';
import {
  currentFloor as selectCurrentFloor,
  currentProject as selectCurrentProject,
  selectCurrentBuilding,
  selectFloorplanImageData,
} from '../projects/selectors';
import {
  bulkUpdateShapes,
  calibrate,
  copyFloorShape,
  createSpaces as createSpacesApi,
  deleteSpaces,
  fetchSuggestedCalibrationApi,
  parseSpaceToAPISpace,
  updateSpaces,
  updateSuggestedCalibrationApi,
} from '../../../api/floorplan';
import { getUsfTotal } from '../../../api/esa-program-summary';
import { selectClientFacingNamesAreVisible } from '../space_types/selectors';
import { IBuilding, IFloor, IProject } from '../projects/types';
import { getMissingProjectData } from '../benchmark-reports/actions';

function floorMapSelector(state: IAppState) {
  return state.floorplanReducer.floorMapState;
}

function individualSpacesCurrentState(state: IAppState) {
  return state.floorplanReducer.currentControlState;
}

function convertBase64ImageToFile(base64Image: string, name: string) {
  const image = base64Image.split(',')[1];
  const byteChars = atob(image);
  const arrayBuffer = new Array(byteChars.length);

  for (let i = 0; i < byteChars.length; i++) {
    arrayBuffer[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(arrayBuffer);

  const imageFileBlob = new Blob([byteArray], {
    type: 'image/png',
  });
  const imageFile = new File([imageFileBlob], name, {
    type: imageFileBlob.type,
  });
  return imageFile;
}

function* resetFloorPlanControls() {
  yield put(enableAllViewLayers());
}

function* attachNote(action: ReturnType<typeof actions.attachNote>) {
  try {
    const { space, text } = action.payload;
    const spaceToUpdate: ISpace = yield select(spaceToUpdateSelector);

    const response: APIResponse<typeof attachNoteApi> = yield call(
      attachNoteApi,
      space.id,
      text
    );

    const { id, name, insertedAt } = response.data.note;

    yield put(attachNoteSucceededAction(id));

    if (spaceToUpdate) {
      const updatedSpace = {
        ...spaceToUpdate,
        notes: [...spaceToUpdate.notes, { id, text, name, insertedAt }],
      };
      yield put(setSpaceToUpdate(updatedSpace));
      const spaceCategory =
        space.projectSpaceType?.category?.defaultSpaceTypeCategory?.category;

      if (spaceCategory) {
        const spacesMap: IMap<string, ISpace[]> = yield select(selectSpacesMap);
        let categorySpaces = _.cloneDeep(spacesMap.get(spaceCategory));
        let updatedSpaceIndex = categorySpaces.findIndex(
          (s) => s.id === space.id
        );
        categorySpaces.splice(updatedSpaceIndex, 1, updatedSpace);
        spacesMap.set(spaceCategory, categorySpaces);
        yield put(getSpacesSucceeded(spacesMap));

        const floorSpaces: Record<string, ISpace[]> = yield select(
          selectFloorSpaces
        );
        categorySpaces = floorSpaces[spaceCategory];
        updatedSpaceIndex = categorySpaces.findIndex((s) => s.id === space.id);

        yield put(
          getFloorSpacesSucceeded({
            ...floorSpaces,
            [spaceCategory]: replaceAtIndex(
              categorySpaces,
              space,
              updatedSpaceIndex
            ),
          })
        );
      }
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* toggleIndividualStates(
  action: ReturnType<typeof toggleIndividualSpaces>
) {
  try {
    const { categoryHasAlternativeWorkpoint } = action.payload;

    const {
      showIndividualWorkSpacesIntermediate,
      showIndividualWorkSpaces,
      showIndividualWorkspacesAltWorkpoints,
    }: ReturnType<typeof individualSpacesCurrentState> = yield select(
      individualSpacesCurrentState
    );

    if (
      categoryHasAlternativeWorkpoint &&
      ((showIndividualWorkSpaces && showIndividualWorkspacesAltWorkpoints) ||
        !showIndividualWorkSpaces)
    ) {
      yield put(
        setControlStateValue(
          'showIndividualWorkspacesAltWorkpoints',
          !showIndividualWorkspacesAltWorkpoints
        )
      );
    }
    if (
      !categoryHasAlternativeWorkpoint ||
      !showIndividualWorkspacesAltWorkpoints
    ) {
      if (!showIndividualWorkSpacesIntermediate && showIndividualWorkSpaces) {
        yield put(toggleWorkspaceIntermediate(true));
      } else {
        yield put(toggleWorkspaceIntermediate(false));
        yield put(toggleIndividualSpacesSuccess());
      }
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* toggleMeetingSpacesStates(
  action: ReturnType<typeof toggleMeetingSpaces>
) {
  try {
    const { categoryHasAlternativeWorkpoint } = action.payload;
    const {
      showMeetingSpacesAltWorkpoints,
      showMeetingSpaces,
    }: IFloorplanControlsState = yield select(selectCurrentControlState);

    if (
      categoryHasAlternativeWorkpoint &&
      ((showMeetingSpaces && showMeetingSpacesAltWorkpoints) ||
        !showMeetingSpaces)
    ) {
      yield put(
        setControlStateValue(
          'showMeetingSpacesAltWorkpoints',
          !showMeetingSpacesAltWorkpoints
        )
      );
    }
    if (!categoryHasAlternativeWorkpoint || !showMeetingSpacesAltWorkpoints)
      yield put(setControlStateValue('showMeetingSpaces', !showMeetingSpaces));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* toggleAmenitySpacesStates(
  action: ReturnType<typeof toggleAmenitySpaces>
) {
  try {
    const { categoryHasAlternativeWorkpoint } = action.payload;
    const {
      showAmenitySpacesAltWorkpoints,
      showAmenitySpaces,
    }: IFloorplanControlsState = yield select(selectCurrentControlState);

    if (
      categoryHasAlternativeWorkpoint &&
      ((showAmenitySpaces && showAmenitySpacesAltWorkpoints) ||
        !showAmenitySpaces)
    ) {
      yield put(
        setControlStateValue(
          'showAmenitySpacesAltWorkpoints',
          !showAmenitySpacesAltWorkpoints
        )
      );
    }
    if (!categoryHasAlternativeWorkpoint || !showAmenitySpacesAltWorkpoints)
      yield put(setControlStateValue('showAmenitySpaces', !showAmenitySpaces));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* toggleSupportSpacesStates(
  action: ReturnType<typeof toggleSupportSpaces>
) {
  try {
    const { categoryHasAlternativeWorkpoint } = action.payload;
    const {
      showSupportSpacesAltWorkpoints,
      showSupportSpaces,
    }: IFloorplanControlsState = yield select(selectCurrentControlState);

    if (
      categoryHasAlternativeWorkpoint &&
      ((showSupportSpaces && showSupportSpacesAltWorkpoints) ||
        !showSupportSpaces)
    ) {
      yield put(
        setControlStateValue(
          'showSupportSpacesAltWorkpoints',
          !showSupportSpacesAltWorkpoints
        )
      );
    }
    if (!categoryHasAlternativeWorkpoint || !showSupportSpacesAltWorkpoints)
      yield put(setControlStateValue('showSupportSpaces', !showSupportSpaces));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* handleSeatCountState() {
  try {
    const floorMapState: ReturnType<typeof floorMapSelector> = yield select(
      floorMapSelector
    );

    if (floorMapState !== FloorMapStates.SeatCount) {
      const seatCountLayer: boolean = yield select(
        (state: IAppState) =>
          state.floorplanReducer.currentControlState.showSeatCountLayer
      );

      if (seatCountLayer) {
        yield put(showSeatCountLayer(false));
        yield put(setFloormapStateAction(FloorMapStates.Show));
        return;
      }

      // If filter has been selected returns to the previous state
      if (
        [FloorMapStates.Note, FloorMapStates.Photo].indexOf(floorMapState) > -1
      ) {
        yield put(restoreControlsStateAction());
      }

      yield put(showSeatCountLayer(false));
      yield put(setControlsStateAction(SEAT_COUNT_ENABLED));
      yield put(setFloormapStateAction(FloorMapStates.SeatCount));
    } else if (
      floorMapState === FloorMapStates.SeatCount ||
      floorMapState === FloorMapStates.SeatCountMidState
    ) {
      yield put(restoreControlsStateAction());
      yield put(setFloormapStateAction(FloorMapStates.Show));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* handleSpaceTypeState(
  _action: ReturnType<typeof actions.spaceTypeClicked>
) {
  try {
    const floorMapState: FloorMapStates = yield select(floorMapSelector);

    if (floorMapState !== FloorMapStates.SpaceType) {
      const spaceTypeLayer: boolean = yield select(
        (state: IAppState) =>
          state.floorplanReducer.currentControlState.showSpaceTypeLayer
      );

      if (spaceTypeLayer) {
        yield put(showSpaceTypeLayer(false));
        yield put(setFloormapStateAction(FloorMapStates.Show));
        return;
      }

      // If filter has been selected returns to the previous state
      if (
        [FloorMapStates.Note, FloorMapStates.Photo].indexOf(floorMapState) > -1
      ) {
        yield put(restoreControlsStateAction());
      }

      yield put(showSpaceTypeLayer(false));
      yield put(setControlsStateAction(SPACE_TYPE_ENABLED));
      yield put(setFloormapStateAction(FloorMapStates.SpaceType));
    } else if (floorMapState === FloorMapStates.SpaceType) {
      yield put(restoreControlsStateAction());
      yield put(setFloormapStateAction(FloorMapStates.Show));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* handleViewSpacesUSFState() {
  try {
    const floorMapState: ReturnType<typeof floorMapSelector> = yield select(
      floorMapSelector
    );

    if (floorMapState !== FloorMapStates.ViewSpacesUSF) {
      yield put(setControlsStateAction(VIEW_SPACES_USF_ENABLED));
      yield put(setFloormapStateAction(FloorMapStates.ViewSpacesUSF));
    } else if (floorMapState === FloorMapStates.ViewSpacesUSF) {
      yield put(restoreControlsStateAction());
      yield put(setFloormapStateAction(FloorMapStates.Show));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

const categoryHasNotes = (
  spacesMap: Map<string, ISpace[]>,
  category: string
): boolean => {
  if (spacesMap.get(category)?.find((space) => !!space.notes.length)) {
    return true;
  }

  return false;
};

const categoryHasPhotos = (
  spacesMap: Map<string, ISpace[]>,
  category: string
): boolean => {
  if (spacesMap.get(category)?.find((space) => !!space.photos.length)) {
    return true;
  }

  return false;
};

function* handlePhotoState(_action: ReturnType<typeof photoFilterClicked>) {
  try {
    const floorMapState: ReturnType<typeof floorMapSelector> = yield select(
      floorMapSelector
    );

    if (floorMapState !== FloorMapStates.Photo) {
      const onlyShowPhotos: boolean = yield select(
        (state: IAppState) =>
          state.floorplanReducer.currentControlState.onlyShowPhotos
      );

      if (onlyShowPhotos) {
        yield put(toggleOnlyWithPhoto(false));
        yield put(setFloormapStateAction(FloorMapStates.Show));
        return;
      }

      // If filter has been selected returns to the previous state
      if (
        [
          FloorMapStates.Note,
          FloorMapStates.SeatCount,
          FloorMapStates.SpaceType,
        ].indexOf(floorMapState) > -1
      ) {
        yield put(restoreControlsStateAction());
      }

      const spacesMap: ReturnType<typeof selectSpacesMap> = yield select(
        selectSpacesMap
      );

      const showIndividualWorkSpaces = [
        INDIVIDUAL_WORKSPACES_CATEGORY,
        WORKSTATIONS_SUBCATEGORY,
        PRIVATE_OFFICES_SUBCATEGORY,
        CUSTOM_SUBCATEGORY,
      ].some((category) => categoryHasPhotos(spacesMap, category));

      yield put(
        setControlsStateAction({
          showIndividualWorkSpaces,
          showMeetingSpaces: categoryHasPhotos(spacesMap, MEETING_SPACES_KEY),
          showAmenitySpaces: categoryHasPhotos(spacesMap, AMENITY_SPACES_KEY),
          showSupportSpaces: categoryHasPhotos(spacesMap, SUPPORT_SPACES_KEY),
          showOtherSpaces: categoryHasPhotos(spacesMap, CUSTOM_CATEGORY),
          showSpacesUSFLayer: false,
          showIndividualWorkspacesAltWorkpoints: false,
          showMeetingSpacesAltWorkpoints: false,
          showAmenitySpacesAltWorkpoints: false,
          showSupportSpacesAltWorkpoints: false,
        })
      );
      yield put(toggleOnlyWithPhoto(true));
      yield put(setFloormapStateAction(FloorMapStates.Photo));
    } else {
      yield put(restoreControlsStateAction());
      yield put(setFloormapStateAction(FloorMapStates.Show));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* handleNotesState(_action: ReturnType<typeof noteFilterClicked>) {
  try {
    const floorMapState: FloorMapStates = yield select(floorMapSelector);

    if (floorMapState !== FloorMapStates.Note) {
      const onlyShowNotes: boolean = yield select(
        (state: IAppState) =>
          state.floorplanReducer.currentControlState.onlyShowNotes
      );

      if (onlyShowNotes) {
        yield put(toggleOnlyWithNote(false));
        yield put(setFloormapStateAction(FloorMapStates.Show));
        return;
      }

      // If other filter has been selected previously returns to the previous state
      if (
        [
          FloorMapStates.Photo,
          FloorMapStates.SeatCount,
          FloorMapStates.SpaceType,
        ].indexOf(floorMapState) > -1
      ) {
        yield put(restoreControlsStateAction());
      }

      const spacesMap: ReturnType<typeof selectSpacesMap> = yield select(
        selectSpacesMap
      );

      const showIndividualWorkSpaces = [
        INDIVIDUAL_WORKSPACES_CATEGORY,
        WORKSTATIONS_SUBCATEGORY,
        PRIVATE_OFFICES_SUBCATEGORY,
        CUSTOM_SUBCATEGORY,
      ].some((category) => categoryHasNotes(spacesMap, category));

      yield put(
        setControlsStateAction({
          showIndividualWorkSpaces,
          showMeetingSpaces: categoryHasNotes(spacesMap, MEETING_SPACES_KEY),
          showAmenitySpaces: categoryHasNotes(spacesMap, AMENITY_SPACES_KEY),
          showSupportSpaces: categoryHasNotes(spacesMap, SUPPORT_SPACES_KEY),
          showOtherSpaces: categoryHasNotes(spacesMap, CUSTOM_CATEGORY),
          showSpacesUSFLayer: false,
          showIndividualWorkspacesAltWorkpoints: false,
          showMeetingSpacesAltWorkpoints: false,
          showAmenitySpacesAltWorkpoints: false,
          showSupportSpacesAltWorkpoints: false,
        })
      );
      yield put(toggleOnlyWithNote(true));
      yield put(setFloormapStateAction(FloorMapStates.Note));
    } else {
      yield put(restoreControlsStateAction());
      yield put(setFloormapStateAction(FloorMapStates.Show));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateSpaceBubblePositionCall(
  action: ReturnType<typeof updateSpaceBubblePosition>
) {
  const { space } = action.payload;
  const measurementState: MeasurementState = yield select(
    selectMeasurementState
  );
  const spacesMap: IMap<string, ISpace[]> = yield select(selectSpacesMap);

  const updatedSpaceCategory: string =
    view(spaceCategoryPath, space) || view(otherSpaceCategoryPath, space);
  const categorySpaces = spacesMap.get(updatedSpaceCategory)?.slice() || [];

  try {
    const updatedSpaceIndex = categorySpaces.findIndex(
      (s) => s.id === space.id
    );

    spacesMap.set(
      updatedSpaceCategory,
      replaceAtIndex(categorySpaces, space, updatedSpaceIndex)
    );

    yield put(getSpacesSucceeded(spacesMap));
    if (isUSFMeasurementState(measurementState)) {
      yield put(setSpaceToUpdate(space));
    }
    yield call(spaceApi.updateSpace, parseSpaceToAPISpace(space), false);
  } catch (e) {
    spacesMap.set(updatedSpaceCategory, categorySpaces);
    yield put(getSpacesSucceeded(spacesMap));
    yield put(handleError(e));
  }
}

function* updateSpace(action: ReturnType<typeof updateSpaceAction>) {
  try {
    const {
      space,
      shouldRefetch,
      shouldRemoveSpaceShape: shouldDeleteSpaceShape,
      shouldUpdateUsf,
      shouldClearActiveShape = true,
    } = action.payload;

    const spacesMap: IMap<string, ISpace[]> = yield select(selectSpacesMap);
    const floorSpaces: Record<string, ISpace[]> = yield select(
      selectFloorSpaces
    );

    if (space) {
      const activeShape: SpaceShapeVM = yield select(selectActiveShape);
      const floorplan: IFloorplan = yield select(selectCurrentFloorplan);

      if (shouldDeleteSpaceShape) {
        const shapes: ISpaceShape[] = yield select(selectAllShapes);
        const shape = _.find(shapes, { isFloorUsf: false, spaceId: space.id });

        if (shape) {
          yield put(deleteShapeAction(shape.id));
          yield take(FETCH_FLOORPLAN_SHAPES);
        }
      }
      if (activeShape?.isConfirmed && !activeShape.id) {
        yield call(
          createShapeApi,
          floorplan,
          omit({ ...activeShape, spaceId: space.id }, ['isConfirmed'])
        );
        yield put(fetchShapesAction());
      } else if (activeShape?.isConfirmed && !!activeShape.id) {
        yield call(updateShapeApi, omit(activeShape, ['isConfirmed']));
        yield put(fetchShapesAction());
      }

      if (shouldClearActiveShape)
        yield put(setActiveShape({ id: 0, isFloorUsf: false, polygons: [] }));

      let {
        data: { updatedSpace },
      } = yield call(
        spaceApi.updateSpace,
        parseSpaceToAPISpace(space),
        shouldUpdateUsf
      );

      const originalSpaceCategory: string = camelCase(
        view(spaceCategoryPath, space) || view(otherSpaceCategoryPath, space)
      );

      const updatedSpaceCategory: string = camelCase(
        view(spaceCategoryPath, updatedSpace) ||
          view(otherSpaceCategoryPath, updatedSpace)
      );

      updatedSpace =
        updatedSpaceCategory === CUSTOM_CATEGORY
          ? {
              ...set(
                otherSpaceCategoryPath,
                updatedSpaceCategory,
                updatedSpace
              ),
            }
          : { ...set(spaceCategoryPath, updatedSpaceCategory, updatedSpace) };

      if (originalSpaceCategory === updatedSpaceCategory) {
        // update the space at the index for its category
        let categorySpaces =
          spacesMap.get(originalSpaceCategory)?.slice() || [];
        let updatedSpaceIndex = categorySpaces.findIndex(
          (s) => s.id === space.id
        );

        const newSpaces: IMap<string, ISpace[]> = new Map(spacesMap);

        newSpaces.set(
          updatedSpaceCategory,
          replaceAtIndex(categorySpaces, updatedSpace, updatedSpaceIndex)
        );

        yield put(getSpacesSucceeded(newSpaces));

        categorySpaces = floorSpaces[updatedSpaceCategory];
        updatedSpaceIndex = categorySpaces.findIndex((s) => s.id === space.id);

        yield put(
          getFloorSpacesSucceeded({
            ...floorSpaces,
            [updatedSpaceCategory]: replaceAtIndex(
              categorySpaces,
              updatedSpace,
              updatedSpaceIndex
            ),
          })
        );
      } else {
        // delete space from old catetgory and add to new one
        let originalCategorySpaces =
          spacesMap.get(originalSpaceCategory)?.slice() || [];
        let originalCategorySpaceIndex = originalCategorySpaces.findIndex(
          (s) => s.id === space.id
        );
        let updatedCategorySpaces =
          spacesMap.get(updatedSpaceCategory)?.slice() || [];

        const newSpaces: IMap<string, ISpace[]> = new Map(spacesMap);

        // remove space from old category in spaces map
        newSpaces.set(
          originalSpaceCategory,
          deleteAtIndex(originalCategorySpaces, originalCategorySpaceIndex)
        );

        // append updated space to new category in spaces map
        newSpaces.set(updatedSpaceCategory, [
          ...updatedCategorySpaces,
          updatedSpace,
        ]);

        yield put(getSpacesSucceeded(newSpaces));

        originalCategorySpaces = floorSpaces[originalSpaceCategory];
        originalCategorySpaceIndex = originalCategorySpaces.findIndex(
          (s) => s.id === space.id
        );
        updatedCategorySpaces = floorSpaces[updatedSpaceCategory] || [];

        yield put(
          getFloorSpacesSucceeded({
            ...floorSpaces,
            [originalSpaceCategory]: deleteAtIndex(
              originalCategorySpaces,
              originalCategorySpaceIndex
            ),
            [updatedSpaceCategory]: [...updatedCategorySpaces, updatedSpace],
          })
        );
      }

      if (shouldRefetch) {
        const project: IProject = yield select(selectCurrentProject);
        yield put(getProjectInfoById(project.id));
      }
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateSpacePosition(
  action: ReturnType<typeof updateSpacePositionAction>
) {
  try {
    const { spaceId, category, previousSpaceId, nextSpaceId } = action.payload;
    const spaceCategory = category;

    const response: APIResponse<typeof spaceApi.updatePosition> = yield call(
      spaceApi.updatePosition,
      spaceId,
      previousSpaceId,
      nextSpaceId
    );

    const spacesMap: IMap<string, ISpace[]> = new Map(
      yield select(selectSpacesMap)
    );

    const categorySpaces = spacesMap.get(spaceCategory)?.slice();

    const { rank } = response.data.updatedSpace;

    const spacePosition = categorySpaces.findIndex(
      (space: ISpace) => space.id === spaceId
    );

    let [selectedSpace] = categorySpaces.splice(spacePosition, 1);
    selectedSpace = { ...selectedSpace, rank };

    const updatedCategorySpaces = categorySpaces;

    if (previousSpaceId) {
      const previousSpacePosition = categorySpaces.findIndex(
        (space: ISpace) => space.id === previousSpaceId
      );
      updatedCategorySpaces.splice(previousSpacePosition + 1, 0, selectedSpace);
    } else {
      updatedCategorySpaces.unshift(selectedSpace);
    }

    const newSpacesMap: IMap<string, ISpace[]> = new Map(spacesMap);
    newSpacesMap.set(spaceCategory, updatedCategorySpaces);

    yield put(getSpacesSucceeded(newSpacesMap));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* confirmAddSpace(action: ReturnType<typeof confirmAddSpaceAction>) {
  try {
    const {
      space,
      notesToAttach,
      shouldNotShowSuccessToast,
      shouldNotSelectSpace,
    } = action.payload;

    const activeShape: SpaceShapeVM = yield select(selectActiveShape);
    let response: APIResponse<
      typeof spaceApi.createSpace | typeof createCluster
    >;
    let entity: ISpace | { id: number };
    if (!typeCheck.isSpace(space) && (space as ICluster).number > 1) {
      const cluster = omit(
        space as ICluster,
        'roomNumber',
        'allowBlankSeats',
        'allowBlankUsf'
      );
      cluster.notes = notesToAttach;
      response = yield call(createCluster, cluster);
      entity = response.data.cluster;
    } else if (typeCheck.isSpace(space)) {
      response = yield call(spaceApi.createSpace, parseSpaceToAPISpace(space));
      entity = response.data.newSpace;

      if (activeShape?.isConfirmed && activeShape?.polygons?.length) {
        const floorplan: IFloorplan = yield select(selectCurrentFloorplan);
        const shape: ISpaceShape = omit(
          { ...activeShape, spaceId: entity.id },
          'isConfirmed'
        );
        yield call(createShapeApi, floorplan, shape);
        yield put(
          setActiveShape({
            id: 0,
            isFloorUsf: false,
            polygons: [],
          })
        );
        yield put(fetchShapesAction());
      }
    }

    if (!shouldNotShowSuccessToast) {
      yield put(
        showToastMessage({
          text: 'The space successfully saved!',
          status: 'success',
        })
      );
    }

    if (entity && typeCheck.isSpace(space) && notesToAttach) {
      yield call(attachNoteApi, entity.id, notesToAttach);
    }

    const project: IProject = yield select(selectCurrentProject);

    yield put(getProjectInfoById(project.id));
    yield put(getSpacesAction(space.floorId));
    if (typeCheck.isSpace(space) && !shouldNotSelectSpace) {
      yield put(setSelectedSpace(entity as ISpace));
    }
    yield put(setFloormapStateAction(FloorMapStates.Show));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* createSpaceFromClusterCall(
  action: ReturnType<typeof createSpaceFromCluster>
) {
  const project: IProject = yield select(selectCurrentProject);

  try {
    const { cluster, space } = action.payload;

    const {
      data: {
        clusterAndSpace: { space: createdSpace },
      },
    } = yield call(createSpaceFromClusterApi, cluster.id, space.floorplanPoint);

    const clusters: ICluster[] = yield select(selectClusters);

    if (cluster.number === 1) {
      yield put(
        populateClusters(clusters.filter((c: ICluster) => c.id !== cluster.id))
      );
    } else {
      const updatedClusterIndex = clusters.findIndex(
        (c) => c.id === cluster.id
      );

      yield put(
        populateClusters(
          replaceAtIndex(
            clusters.slice(),
            { ...cluster, number: cluster.number - 1 },
            updatedClusterIndex
          )
        )
      );
    }

    const spacesMap: Map<string, ISpace[]> = yield select(selectSpacesMap);
    const floorSpaces: Record<string, ISpace[]> = yield select(
      selectFloorSpaces
    );

    const spaceCategory: string = camelCase(view(spaceCategoryPath, space));
    const categorySpaces = spacesMap.get(spaceCategory)?.slice() || [];

    const newSpace = set(spaceCategoryPath, spaceCategory, createdSpace);
    categorySpaces.push(newSpace);

    const newSpacesMap = new Map(spacesMap);
    newSpacesMap.set(spaceCategory, categorySpaces);

    yield put(getSpacesSucceeded(newSpacesMap));

    yield put(
      getFloorSpacesSucceeded({
        ...floorSpaces,
        [spaceCategory]: [...(floorSpaces[spaceCategory] || []), newSpace],
      })
    );

    if (project?.status === 'benchmark') {
      yield put(getMissingProjectData(project.id));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* addSpace(_action: ReturnType<typeof addSpaceAction>) {
  try {
    // turn off everything else
    yield put(setFloormapStateAction(FloorMapStates.AddSpaceType));
    yield put(setMeasurementState(MeasurementState.NONE)); // turn off any measurement shapes
    yield resetFloorPlanControls();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getSpaceToUpdate(
  action: ReturnType<typeof actions.getSpaceToUpdate>
) {
  try {
    // turn off everything else
    yield put(setFloormapStateAction(FloorMapStates.EditSpaceType));
    yield put(setMeasurementState(MeasurementState.NONE)); // turn off any measurement shapes
    yield resetFloorPlanControls();
    const { space } = action.payload;
    const response: APIResponse<typeof getSpaceApi> = yield call(
      getSpaceApi,
      space.id
    );

    if (response.data) {
      let spaceToUpdate = response.data.space;

      spaceToUpdate = set(
        spaceCategoryPath,
        camelCase(view(spaceCategoryPath, spaceToUpdate)),
        spaceToUpdate
      );

      spaceToUpdate.isCluster = space.isCluster;
      yield put(setSpaceToUpdate(spaceToUpdate));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteSpace(action: ReturnType<typeof actions.deleteSpace>) {
  try {
    const { spaceId } = action.payload;
    const response: APIResponse<typeof deleteSpaceApi> = yield call(
      deleteSpaceApi,
      spaceId
    );
    const space = response.data.deleteSpace;
    const currentProject: IProject = yield select(selectCurrentProject);
    yield all([
      put(getSpacesAction(space.floorId)),
      put(getProjectInfoById(currentProject.id)),
    ]);
    // reset to show state on floor maps once shape is deleted
    yield put(setFloormapStateAction(FloorMapStates.Show));
  } catch (error) {
    yield put(handleError(error));
  } finally {
    const { callback } = action.payload;
    if (callback) {
      callback();
    }
  }
}

function* deleteNote(action: ReturnType<typeof actions.deleteNote>) {
  try {
    const { noteId } = action.payload;
    const spaceToUpdate: ISpace = yield select(spaceToUpdateSelector);

    yield call(deleteNoteApi, noteId);
    const floor: IFloor = yield select(selectCurrentFloor);

    if (spaceToUpdate) {
      yield put(
        setSpaceToUpdate({
          ...spaceToUpdate,
          notes: spaceToUpdate.notes.filter(
            (note: INote) => note.id !== noteId
          ),
        })
      );
    }
    yield put(getSpacesAction(floor.id));
  } catch (error) {
    yield put(handleError(error));
  }
}

function* updateNote(action: ReturnType<typeof actions.updateNote>) {
  try {
    const { noteId, newNote } = action.payload;
    const spaceToUpdate: ISpace = yield select(spaceToUpdateSelector);

    const {
      data: { updateNote: note },
    } = yield call(updateNoteApi, noteId, newNote);

    if (spaceToUpdate && note.spaceId === spaceToUpdate.id) {
      const updatedNoteIndex = spaceToUpdate.notes.findIndex(
        (spaceNote: INote) => spaceNote.id === note.id
      );

      const { notes } = spaceToUpdate;

      yield put(
        setSpaceToUpdate({
          ...spaceToUpdate,
          notes: [
            ...notes.slice(0, updatedNoteIndex),
            note,
            ...notes.slice(updatedNoteIndex + 1),
          ],
        })
      );
    }

    const floor: IFloor = yield select(selectCurrentFloor);
    yield put(getSpacesAction(floor.id));
  } catch (error) {
    yield put(handleError(error));
  }
}

function* setAddFloorNoteState() {
  try {
    yield put(setFloormapStateAction(FloorMapStates.AddFloorNote));
    yield put(setMeasurementState(MeasurementState.NONE)); // turn off any measurement shapes
    yield resetFloorPlanControls();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* fetchFloorplanGeoJson(
  action: ReturnType<typeof actions.fetchFloorplanGeoJson>
) {
  try {
    const { floorId } = action.payload;
    const {
      data: { getFloorplanGeojson: encodedZippedGeoJson },
    } = yield call(floorPlanApi.downloadFloorplanGeoJson, floorId);
    const blob = convertBase64StringToBlob(encodedZippedGeoJson, 'zip');
    const zipFileReader = new BlobReader(blob);
    const stream = new TransformStream();
    const streamTextPromise = new Response(stream.readable).text();
    const zipReader = new ZipReader(zipFileReader);
    const entries: Entry[] = yield zipReader.getEntries();
    yield entries.shift().getData(stream.writable);
    yield zipReader.close();
    const serializedGeoJson: string = yield streamTextPromise;
    const geoJson = JSON.parse(serializedGeoJson);
    yield put(setFloorplanGeoJson(geoJson));
  } catch (e) {
    if (e.message === 'not_found') {
      yield put(setFloorplanGeoJson(null));
      return;
    }
    yield put(handleError(e));
  }
}

function* fetchFloorplan(action: ReturnType<typeof actions.fetchFloorplan>) {
  try {
    const { floorId } = action.payload;
    yield put(populateCalibration(null));
    const floorplanResponse: APIResponse<typeof floorPlanApi.fetchFloorplan> = yield call(
      floorPlanApi.fetchFloorplan,
      floorId
    );

    const floorplan = camelize(floorplanResponse.data.floorplan);

    yield put(fetchFloorplanSucceeded(floorplan));
    yield put(fetchCalibrationAction());
    yield put(fetchSuggestedCalibration());
    yield put(fetchShapesAction());
  } catch (e) {
    if (e.message === 'not_found') {
      const { floorId } = action.payload;
      // This is intentional, as this actions can fail when no floorplan has been uploaded
      yield put(fetchFloorplanSucceeded({ id: 0, floorId, s3Path: null }));
    } else {
      yield put(handleError(e));
    }
  } finally {
    const floorplanRequestStatus: REQUEST_STATUS = yield select(
      selectFloorplanRequestStatus
    );

    if (floorplanRequestStatus !== REQUEST_STATUS.SUCCESS) {
      yield put(fetchFloorplanFailed());
    }
  }
}

function* fetchFloorplanUploadCall(
  action: ReturnType<typeof actions.fetchFloorplanUpload>
) {
  try {
    const { floorId } = action.payload;
    const floorplanUploadResponse: APIResponse<typeof floorPlanApi.fetchFloorplanUpload> = yield call(
      floorPlanApi.fetchFloorplanUpload,
      floorId
    );

    const { floorplanUpload } = floorplanUploadResponse.data;

    yield put(setCurrentFloorplanUpload(floorplanUpload));
  } catch (e) {
    if (e.message === 'not_found') {
      yield put(
        setCurrentFloorplanUpload({
          id: -1,
          status: 'done',
          geojsonStatus: FLOORPLAN_UPLOAD_STATUS.FAILED,
        })
      );
    } else {
      yield put(handleError(e));
    }
  }
}

function* uploadFloorplan(action: ReturnType<typeof actions.uploadFloorplan>) {
  try {
    const { image, floor } = action.payload;
    const currentFloorPlan: ReturnType<typeof selectCurrentFloorplan> = yield select(
      selectCurrentFloorplan
    );

    let floorplan;

    if (currentFloorPlan && currentFloorPlan.id) {
      const response: APIResponse<typeof updateFloorplanApi> = yield call(
        updateFloorplanApi,
        floor.id,
        image
      );

      floorplan = camelize(response.data.floorplan);

      const {
        spaces: adjustedSpaces,
        shapes: adjustedShapes,
        calibration: adjustedCalibration,
        shouldDeleteCalibration,
      }: IAdjustedFloorplanItems = yield select(selectAdjustedFloorplanItems);
      if (shouldDeleteCalibration) {
        yield call(deleteFloorCalibration, currentFloorPlan.id);
        yield put(fetchSuggestedCalibration());
      } else {
        if (adjustedShapes) {
          yield call(bulkUpdateShapes, {
            floorId: floor.id,
            floorplanId: currentFloorPlan.id,
            shapes: (adjustedShapes as any).map((shape: ISpaceShape) => {
              return {
                ...shape,
                polygons: latLngsToMultiPolygonPoints(shape.polygons),
              };
            }),
          });
        }
        if (adjustedSpaces && adjustedSpaces?.length > 0) {
          yield call(
            updateSpaces,
            adjustedSpaces.map((space) => {
              const spaceHasShape =
                adjustedShapes &&
                adjustedShapes.find(({ spaceId }) => spaceId === space.id);
              return spaceHasShape
                ? _.omit(parseSpaceToAPISpace(space), 'usf')
                : parseSpaceToAPISpace(space);
            })
          );
        }
        if (adjustedCalibration) {
          yield call(calibrate, currentFloorPlan, adjustedCalibration);
        }
      }
      yield put(fetchCalibrationAction());

      yield put(fetchShapesAction());
      yield put(setDidUpdateFloorPlan(true));
    } else {
      const response: APIResponse<typeof floorPlanApi.createFloorplan> = yield call(
        floorPlanApi.createFloorplan,
        floor.id,
        image
      );

      floorplan = camelize(response.data.floorplan);

      yield put(refreshCurrentProject());
    }

    if (isNil(floorplan)) {
      throw new Error('Failed to upload floor plan');
    }

    const currentFloor: ReturnType<typeof selectCurrentFloor> = yield select(
      selectCurrentFloor
    );

    if (currentFloor?.id === floor?.id) {
      const { data }: APIResponse<typeof getUsfTotal> = yield call(
        getUsfTotal,
        {
          floorIds: [floor.id],
          buildingIds: [],
        }
      );
      if (data?.usfTotal?.total) {
        currentFloor.totalFloorUsf = data.usfTotal.total;
        yield put(setCurrentFloor(currentFloor));
      }

      yield put(uploadFloorplanSucceeded(floorplan));
    }

    yield put(fetchSuggestedCalibration());
  } catch (e) {
    const isLoadingFloorplan: boolean = yield select(
      selectIsUploadingFloorplan
    );
    if (isLoadingFloorplan) {
      yield put(uploadFloorplanFailed());
    }
    yield put(handleError(e));
  } finally {
    yield put(setFloorImageAction(null));

    const floorplan: ReturnType<typeof selectCurrentFloorplan> = yield select(
      selectCurrentFloorplan
    );

    if (floorplan?.id) {
      yield put(archiveFloorplan(floorplan.floorId));
    }
  }
}

function* cropFloorplanImage(
  action: ReturnType<typeof actions.cropFloorplanImage>
) {
  try {
    const { cropBounds } = action.payload;
    const previewImage: string = yield select(selectFloorplanImageData);
    const previewImageBounds: L.LatLngBounds = yield select(
      selectFloorplanPreviewImageBounds
    );
    const [nw, ne, sw] = [
      cropBounds.getNorthWest(),
      cropBounds.getNorthEast(),
      cropBounds.getSouthWest(),
    ];
    const imageHeight = previewImageBounds.getNorth();
    const imageFile = convertBase64ImageToFile(previewImage, 'preview.png');
    // cropzone here has origin at top left of original image
    const cropZone = {
      top: Math.round(imageHeight - nw.lat),
      left: Math.round(nw.lng),
      width: Math.round(ne.lng - nw.lng),
      height: Math.round(nw.lat - sw.lat),
    };
    const imageBitmap: ImageBitmap = yield createImageBitmap(
      imageFile,
      cropZone.left,
      cropZone.top,
      cropZone.width,
      cropZone.height
    );
    const canvas = document.createElement('canvas');
    canvas.width = imageBitmap.width;
    canvas.height = imageBitmap.height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(imageBitmap, 0, 0);

    const floorPlan = canvas.toDataURL();
    if (isNil(floorPlan)) {
      throw new Error('Failed to upload floor plan');
    }
    yield put(setFloorImageAction(floorPlan));
    yield put(setFloorplanPreviewState(null));
    yield put(
      addItemToEditHistory({
        action: EditAction.Crop,
        data: cropZone,
      })
    );
    canvas.remove();
  } catch (e) {
    Sentry.captureException(e);
  }
}

function* archiveFloorplanCall(
  action: ReturnType<typeof actions.archiveFloorplan>
) {
  try {
    const { floorId } = action.payload;
    yield put(setCurrentFloorplanUpload(null));
    const originalFileDataUrl: ReturnType<typeof selectOriginalFileDataUrl> = yield select(
      selectOriginalFileDataUrl
    );

    const editHistory: EditHistory = yield select(selectEditHistory);
    const originalImageDimensions: {
      width: number;
      height: number;
    } = yield select(selectOriginalImageDimensions);

    const originalImage = convertBase64ImageToFile(
      originalFileDataUrl,
      'originalFloorplan.png'
    );

    const compressedHistory = compressEditHistory(editHistory.commands);

    const response: APIResponse<typeof floorPlanApi.archiveFloorplan> = yield call(
      floorPlanApi.archiveFloorplan,
      floorId,
      originalImage,
      compressedHistory,
      originalImageDimensions
    );

    const { floorplanUpload } = response.data;

    yield put(setCurrentFloorplanUpload(floorplanUpload));
    yield put(setOriginalFileDataUrl(''));
  } catch (e) {
    yield put(handleError(e));
  } finally {
    yield put(setFloorplanGeoJson(null));
    yield put(setOriginalImageDimensions(null));
    yield put(cleanEditHistory());
    yield put(setIsArchivingFloorplan(false));
  }
}

function* uploadPDF(floorId: number, pdf: File) {
  try {
    yield put(setIsUploadingPDF(true));
    // Convert data to base64
    // response returns data as string image
    const response: APIResponse<typeof uploadFloorplanPDF> = yield call(
      uploadFloorplanPDF,
      floorId,
      pdf
    );
    // load string image for manipulation in img editor
    const floorPlan = camelize(response.data.createFloorplanPdf);
    if (isNil(floorPlan)) {
      throw new Error('Failed to upload floor plan');
    }
    yield put(setFloorImageAction(floorPlan));
  } catch (e) {
    Sentry.captureException(e);
  } finally {
    yield put(setIsUploadingPDF(false));
  }
}

function* startPDFUpload(action: ReturnType<typeof startPDFUploadAction>) {
  const { floorId, pdf } = action.payload;
  // Race will cancel the uploadPDF task if the CANCEL_PDF_UPLOAD action is dispatched
  yield race([call(uploadPDF, floorId, pdf), take(CANCEL_PDF_UPLOAD)]);
}

function* saveFloorImage() {
  try {
    const imageData: ReturnType<typeof selectFloorplanImageData> = yield select(
      selectFloorplanImageData
    );
    const floor: IFloor = yield select(selectCurrentFloor);

    if (isNil(imageData)) {
      yield put(handleError(new Error('Upload an image first')));
    }
    if (isNil(floor)) {
      yield put(handleError(new Error('A floor needs to be selected first')));
    }

    yield put(setFloorImageAction(imageData));
    const imageFile = convertBase64ImageToFile(imageData, 'floorplan.png');

    // upload image to database
    yield put(uploadFloorplanAction(imageFile, floor));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getFloorSpaces(action: ReturnType<typeof actions.getSpaces>) {
  try {
    const spacesResponse: APIResponse<typeof getFloorSpacesApi> = yield call(
      getFloorSpacesApi,
      action.payload.floorId
    );

    const floorSpaces = camelize(spacesResponse.data.floorSpaces);

    const { spacesMap, spacesGroupedByCategory } = buildSpacesMap(floorSpaces);

    yield put(getSpacesSucceeded(spacesMap));
    yield put(getFloorSpacesSucceeded(spacesGroupedByCategory));

    const currentSpace: ISpace = yield select(
      (state: IAppState) => state.floorplanReducer.spaceToUpdate
    );

    if (currentSpace) {
      const getSpacesFromMap = _.flatten([...spacesMap.values()]);
      const space = getSpacesFromMap.find((x) => x.id === currentSpace.id);
      yield put(setSpaceToUpdate(space));
    }
    // load clusters
    const clusterResponse: APIResponse<typeof getClusters> = yield call(
      getClusters,
      action.payload.floorId
    );
    const { clusters } = clusterResponse.data;

    if (clusters) {
      // populate clusters onto floorplan map
      yield put(populateClusters(camelize(_.map(clusters, camelize))));
    }
  } catch (error) {
    yield put(handleError(error));
  }
}

// unattached note means that it isn't attached to a space

function* addUnattachedNote(
  action: ReturnType<typeof actions.addUnattachedNote>
) {
  try {
    const { note } = action.payload;
    const categories: ISpaceTypeCategory[] = yield select(
      (state: IAppState) => state.projectsReducer.spaceTypeCategories
    );
    const customCategory: ISpaceTypeCategory = find(
      categories,
      (spaceTypeCategory: ISpaceTypeCategory) =>
        spaceTypeCategory.category === camelCase(CUSTOM_CATEGORY)
    );

    const space: IMinimalSpace = {
      floorId: note.floorId,
      floorplanPoint: note.position,
      spaceTypeId: customCategory.spaceTypes[0].id,
      roomName: note.spaceName,
    };
    const newSpaceResponse: APIResponse<typeof spaceApi.createSpace> = yield call(
      spaceApi.createSpace,
      space
    );
    const { newSpace } = newSpaceResponse.data;
    const project: ReturnType<typeof selectCurrentProject> = yield select(
      selectCurrentProject
    );

    yield call(attachNoteApi, newSpace.id, note.text);
    yield put(getProjectInfoById(project.id));
    yield put(getSpacesAction(note.floorId));
  } catch (error) {
    yield put(handleError(error));
  }
}

function* createFloorCalibration(
  action: ReturnType<typeof actions.populateCalibration>
) {
  const currentFloorPlan: ReturnType<typeof selectCurrentFloorplan> = yield select(
    selectCurrentFloorplan
  );
  yield put(populateCalibration(null));
  const calibration: ICalibration = action.payload;
  const suggestedCalibration: SuggestedCalibration = yield select(
    selectSuggestedCalibration
  );

  const floor: IFloor = yield select(selectCurrentFloor);
  const currentProject: IProject = yield select(selectCurrentProject);

  try {
    if (currentFloorPlan) {
      if (calibration) {
        if (isSuggestedCalibrationValid(suggestedCalibration)) {
          calibration.suggestionAcceptedByUser = areCalibrationLinesEqual(
            calibration,
            suggestedCalibration
          );
        }

        // create the new calibration (deletes the old one)
        yield call(calibrate, currentFloorPlan, calibration);
        yield put(fetchCalibrationAction());
        yield put(
          showToastMessage({
            text: 'The floor is calibrated!',
            status: 'success',
          })
        );
      } else {
        // delete the current calibration for the floorplan
        yield call(deleteFloorCalibration, currentFloorPlan.id);
        yield put(fetchCalibrationSucceeded(null));
        yield put(fetchSuggestedCalibration());

        // Clear out floor usf in state
        const cleanFloor: IFloor = {
          ...floor,
          totalFloorUsf: null,
          isMeasuredFromFloorplan: false,
        };
        const currentBuilding: IBuilding = yield select(selectCurrentBuilding);
        yield all([
          put(
            setCurrentBuilding(
              {
                ...currentBuilding,
                floors: currentBuilding.floors.map((f) =>
                  f.id === floor.id ? cleanFloor : f
                ),
              },
              true
            )
          ),
          put(setCurrentFloor(cleanFloor)),
        ]);
      }
      yield all([
        put(getProjectInfoById(currentProject.id)),
        put(fetchShapesAction()),
        put(getSpaces(floor.id)),
      ]);
    }
  } catch (error) {
    yield put(fetchCalibrationAction());
    yield put(handleError(error));
  }
}

function* fetchCalibration() {
  try {
    const floorplan: ReturnType<typeof selectCurrentFloorplan> = yield select(
      selectCurrentFloorplan
    );
    if (floorplan) {
      const calibrationResponse: APIResponse<typeof fetchFloorplanCalibrationApi> = yield call(
        fetchFloorplanCalibrationApi,
        floorplan.id
      );
      const calibration = camelize(calibrationResponse.data.calibration);

      yield put(fetchCalibrationSucceeded(calibration));
    }
  } catch (e) {
    Sentry.captureException(e);
    // This is intentional, as this actions can fail when no calibration has been uploaded
    yield put(populateCalibration(null));
  }
}

function* fetchSuggestedCalibrationCall() {
  try {
    const floorplan: ReturnType<typeof selectCurrentFloorplan> = yield select(
      selectCurrentFloorplan
    );

    if (floorplan) {
      const {
        data,
      }: APIResponse<typeof fetchSuggestedCalibrationApi> = yield call(
        fetchSuggestedCalibrationApi,
        floorplan.id
      );
      const { suggestedCalibration } = data;
      yield put(fetchSuggestedCalibrationSucceeded(suggestedCalibration));
    }
  } catch (e) {
    Sentry.captureException(e);
    yield put(fetchSuggestedCalibrationSucceeded(null));
  }
}

function* updateSuggestedCalibrationCall(
  action: ReturnType<typeof actions.updateSuggestedCalibration>
) {
  const currentSuggestedCalibration: SuggestedCalibration = yield select(
    selectSuggestedCalibration
  );

  try {
    const suggestedCalibration: SuggestedCalibration = action.payload;
    yield put(setSuggestedCalibration(suggestedCalibration));

    const floorplan: ReturnType<typeof selectCurrentFloorplan> = yield select(
      selectCurrentFloorplan
    );

    yield call(updateSuggestedCalibrationApi, floorplan, suggestedCalibration);
  } catch (e) {
    yield put(setSuggestedCalibration(currentSuggestedCalibration));
    yield put(handleError(e));
  }
}

function* createShape() {
  yield put(setMeasurementState(MeasurementState.LOADING));
  try {
    const shape: ReturnType<typeof selectActiveShape> = yield select(
      selectActiveShape
    );
    const areaUnit: string = yield select(selectAreaUnit);
    if (isEmptyShape(shape)) {
      yield put(setMeasurementState(MeasurementState.NONE));
      return;
    }
    if (shape.id) {
      yield put(updateShapeAction());
      return;
    }
    const floorplan: ReturnType<typeof selectCurrentFloorplan> = yield select(
      selectCurrentFloorplan
    );
    if (shape && floorplan) {
      yield call(createShapeApi, floorplan, shape);
      if (shape.isFloorUsf) {
        yield put(
          showToastMessage({
            text: `Floor ${areaUnit} saved successfully!`,
            status: 'success',
          })
        );
        yield put(setIsMeasuredShapeDirty(false));
      }

      yield put(setActiveShape(null));
      yield put(fetchShapesAction());
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* fetchShapes(action: ReturnType<typeof fetchShapesAction>) {
  try {
    const floorplan: ReturnType<typeof selectCurrentFloorplan> = yield select(
      selectCurrentFloorplan
    );
    if (floorplan) {
      const shapesResponse: APIResponse<typeof fetchShapesApi> = yield call(
        fetchShapesApi,
        floorplan.id
      );
      let shapes = camelize(shapesResponse.data.shapes);
      shapes = _.map(shapes, (shape: IShapeResponse) =>
        removeTypeNameFromObject({
          ...shape,
          polygons: unPackPolygonsMatrixForPersistence(shape.polygons),
        })
      );
      yield put(populateFloorplanShapes(shapes));
    }
    if (action.payload?.measurementStateAfterFetching) {
      yield put(
        setMeasurementState(action.payload?.measurementStateAfterFetching)
      );
    } else {
      yield put(setMeasurementState(MeasurementState.NONE));
    }
  } catch (e) {
    yield put(handleError(e));
    yield put(populateFloorplanShapes([]));
  }
}

function* updateShape() {
  try {
    const shape: ReturnType<typeof selectActiveShape> = yield select(
      selectActiveShape
    );
    const areaUnit: string = yield select(selectAreaUnit);
    if (shape?.id) {
      yield call(updateShapeApi, shape);
      const shapes: ReturnType<typeof selectAllShapes> = yield select(
        selectAllShapes
      );
      yield put(
        showToastMessage({
          text: `Floor ${areaUnit} saved successfully!`,
          status: 'success',
        })
      );
      const shapePreviouslyAssignedToSpace: ISpaceShape = _.find(shapes, {
        spaceId: shape.spaceId,
      });
      if (
        shapePreviouslyAssignedToSpace &&
        shapePreviouslyAssignedToSpace.spaceId !== shape.spaceId
      ) {
        yield call(updateShapeApi, {
          ...shapePreviouslyAssignedToSpace,
          spaceId: null,
        });
      }
    }
    const calibration: ReturnType<typeof selectCalibration> = yield select(
      selectCalibration
    );
    const area = getAreaOfShape(shape, calibration);
    const floor: IFloor = yield select(selectCurrentFloor);

    if (shape.isFloorUsf) {
      yield put(
        updateBuildingFloor({
          ...floor,
          totalFloorUsf: area,
        })
      );
    } else {
      const spaceTypesMap: ReturnType<typeof selectSpacesMap> = yield select(
        selectSpacesMap
      );
      const spaces = _.flatten([...spaceTypesMap.values()]);
      const space = _.find(spaces, { id: shape.spaceId });
      if (space) {
        yield put(
          updateSpaceAction(
            { ...space, usf: area, floorId: floor.id },
            { shouldRefetch: true }
          )
        );
      }
    }

    yield put(setActiveShape(null));
    yield put(setMeasurementState(MeasurementState.NONE));
    yield put(fetchShapesAction());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteShape() {
  yield put(setMeasurementState(MeasurementState.LOADING));
  try {
    const shape: ReturnType<typeof selectActiveShape> = yield select(
      selectActiveShape
    );
    if (shape?.id) {
      yield call(deleteShapeApi, shape.id);
      yield put(setActiveShape(null));
      yield put(fetchShapesAction());
    }
    const floor: IFloor = yield select(selectCurrentFloor);

    if (shape.spaceId) {
      const spaceTypesMap: ReturnType<typeof selectSpacesMap> = yield select(
        selectSpacesMap
      );
      const spaces = _.flatten([...spaceTypesMap.values()]);
      const space = _.find(spaces, { id: shape.spaceId });
      if (space) {
        yield put(
          updateSpaceAction(
            { ...space, usf: 0, floorId: floor.id },
            { shouldRefetch: true }
          )
        );
      }
    }
    if (shape.isFloorUsf) {
      yield put(
        updateBuildingFloor(
          {
            ...floor,
            totalFloorUsf: 0,
            totalFloorRsf: floor.isRsfTotalLocked ? floor.totalFloorRsf : null,
            rsfLoadFactor: floor.isRsfTotalLocked ? 0 : floor.rsfLoadFactor,
          },
          false
        )
      );
    }
    yield put(setMeasurementState(MeasurementState.NONE));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteShapeOnUpdateSpaceHook(
  action: ReturnType<typeof actions.deleteShape>
) {
  try {
    yield call(deleteShapeApi, action.payload.id);
    yield put(fetchShapesAction());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* copyMeasuredFloorUsf(
  action: ReturnType<typeof actions.copyMeasuredFloorUsf>
) {
  try {
    const { floorplanId }: SpaceShapeVM = yield select(
      selectFloorUsfShapeToCopy
    );
    const {
      inputPolygons,
      calibration,
      targetFloor,
      callback,
    } = action.payload;

    yield call(
      copyFloorShape,
      inputPolygons,
      _.pick(calibration, ['constant', 'x1', 'y1', 'x2', 'y2']),
      floorplanId,
      targetFloor.floorplan.id
    );

    callback();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateCluster(action: ReturnType<typeof actions.updateCluster>) {
  const clusters: ReturnType<typeof selectClusters> = yield select(
    selectClusters
  );

  try {
    const { cluster } = action.payload;

    const updatedClusterIndex = clusters.findIndex((c) => c.id === cluster.id);

    yield put(
      populateClusters(
        replaceAtIndex(clusters.slice(), cluster, updatedClusterIndex)
      )
    );

    // remove unnecessary fields automatically generated for a space
    // as these don't apply to clusters
    yield call(
      updateClusterApi,
      _.omit(_.cloneDeep(action.payload.cluster), [
        'notes',
        'typename',
        'floorplanPoint.typename',
        'spaceType',
        'isCluster',
        'projectSpaceType',
      ])
    );
  } catch (e) {
    yield put(populateClusters(clusters));
    yield put(handleError(e));
  }
}

function* deleteCluster(action: ReturnType<typeof deleteClusterAction>) {
  const clusters: ICluster[] = yield select(selectClusters);
  try {
    const { id } = action.payload;

    const response: APIResponse<typeof deleteClusterApi> = yield call(
      deleteClusterApi,
      action.payload.id
    );
    if (response.data.deleteCluster) {
      const index = _.findIndex(clusters, { id });
      if (index > -1) {
        const newClusters = cloneDeep(clusters);
        newClusters.splice(index, 1);
        yield put(populateClusters(newClusters));
        yield put(deleteClusterSucceded());
      }
    }
  } catch (e) {
    yield put(deleteClusterFailed());
    yield put(handleError(e));
  }
}

function* deleteSelectedSpacesCall() {
  try {
    const selectedSpaces: ISpace[] = yield select(selectSelectedSpaces);
    const currentProject: IProject = yield select(selectCurrentProject);
    yield call(
      deleteSpaces,
      flatten(selectedSpaces).map((s) => s.id)
    );
    yield put(setSelectedSpaceLayers([]));
    const currentFloor: IFloor = yield select(selectCurrentFloor);
    const floorMapState: FloorMapStates = yield select(selectFloorMapState);
    const measurementStateAfterFetching =
      floorMapState === FloorMapStates.ViewSpacesUSF
        ? MeasurementState.DISPLAY_SHAPES
        : MeasurementState.NONE;
    yield all([
      put(getSpaces(currentFloor.id)),
      put(fetchShapesAction({ measurementStateAfterFetching })),
      put(getProjectInfoById(currentProject.id)),
    ]);
  } catch (e) {
    yield put(handleError(e));
  }
}

function* createSpaces(action: ReturnType<typeof createSpacesAction>) {
  try {
    const { spaces } = action.payload;
    const floorMapState: FloorMapStates = yield select(selectFloorMapState);
    const currentFloor: IFloor = yield select(selectCurrentFloor);
    const currentFloorplan: IFloorplan = yield select(selectCurrentFloorplan);
    const currentProject: IProject = yield select(selectCurrentProject);
    const targetFloorCalibration: ICalibration = yield select(
      selectCalibration
    );
    const sourceFloorCalibration: ICalibration = yield select(
      selectBulkOperationSourceFloorCalibration
    );

    const createdShapes = spaces
      .filter((space: ISpace) => space.shape)
      .map((space: ISpace) => {
        return {
          ..._.omit(space.shape, 'markerId', 'drawnShape'),
          isNewlyCreatedShape: true,
          space: _.omit(space, 'shape'),
        };
      });
    const allShapes: ISpaceShape[] = yield select(selectAllShapes);
    if (createdShapes.length > 0) {
      const newShapes = [...allShapes, ...createdShapes];
      yield put(populateFloorplanShapes(newShapes));
    }
    const createSpacesResponse: APIResponse<typeof createSpacesApi> = yield call(
      createSpacesApi,
      {
        sourceFloorId: spaces[0].floorId,
        spaces: spaces.map(
          (s) =>
            omit(
              {
                ...parseSpaceToAPISpace(s),
                roomName: undefined,
                roomNumber: undefined,
                floorId: currentFloor.id,
                shape: s.shape
                  ? {
                      floorplanId: currentFloorplan.id,
                      isFloorUsf: false,
                      polygons: latLngsToMultiPolygonPoints(s.shape.polygons),
                    }
                  : null,
              },
              ['allowBlankSeats', 'allowBlankUsf']
            ) as any
        ),
      }
    );
    const createdSpaces = camelize(createSpacesResponse.data.createSpaces).map(
      (space: ISpace) => {
        return { ...space, justCreated: true };
      }
    );
    const { spacesMap } = buildSpacesMap(createdSpaces);
    yield put(getSpacesSucceeded(spacesMap));

    const measurementStateAfterFetching =
      floorMapState === FloorMapStates.ViewSpacesUSF
        ? MeasurementState.DISPLAY_SHAPES
        : MeasurementState.NONE;

    yield put(fetchShapesAction({ measurementStateAfterFetching }));
    yield put(getSpaces(currentFloor.id));
    yield put(getProjectInfoById(currentProject.id));
    yield put(setSelectedSpaceLayers([]));

    if (sourceFloorCalibration && !targetFloorCalibration) {
      yield put(fetchCalibrationAction());
      yield put(
        showToastMessage({
          text: 'The floor is calibrated!',
          status: 'success',
        })
      );
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateMultipleSpacesCall(
  action: ReturnType<typeof updateMultipleSpaces>
) {
  try {
    if (!action.payload.options?.markersShouldRemainSelected) {
      yield put(setSelectedSpaceLayers([]));
    }
    const currentFloor: IFloor = yield select(selectCurrentFloor);
    const currentFloorPlan: ReturnType<typeof selectCurrentFloorplan> = yield select(
      selectCurrentFloorplan
    );
    const updatedShapes = action.payload.options?.spaceShapes;
    if (updatedShapes?.length > 0) {
      const shapesToUpdate = (updatedShapes as any).map(
        (shape: ISpaceShape) => {
          return {
            ..._.omit(shape, 'markerId', 'drawnShape'),
            polygons: latLngsToMultiPolygonPoints(shape.polygons),
          };
        }
      );
      const newlyUpdatedShapes = shapesToUpdate.map((shape: IShapeResponse) =>
        removeTypeNameFromObject({
          ...shape,
          polygons: unPackPolygonsMatrixForPersistence(shape.polygons),
        })
      );
      let allShapes: ISpaceShape[] = yield select(selectAllShapes);
      allShapes = allShapes.filter(
        (currentShape) =>
          !newlyUpdatedShapes.find(
            ({ id }: ISpaceShape) => id === currentShape.id
          )
      );
      const newShapes = [...allShapes, ...newlyUpdatedShapes];
      yield put(populateFloorplanShapes(newShapes));
      yield call(bulkUpdateShapes, {
        floorId: currentFloor.id,
        floorplanId: currentFloorPlan.id,
        shapes: shapesToUpdate,
      });
    }
    yield call(
      updateSpaces,
      action.payload.spaces.map((space) => {
        const spaceHasShape =
          updatedShapes &&
          updatedShapes.find(({ spaceId }) => spaceId === space.id);
        return spaceHasShape
          ? _.omit(parseSpaceToAPISpace(space), 'usf')
          : parseSpaceToAPISpace(space);
      })
    );
    if (action.payload.options?.markersShouldRemainSelected) return;
    const currentProject: IProject = yield select(selectCurrentProject);
    const floorMapState: FloorMapStates = yield select(selectFloorMapState);
    const measurementStateAfterFetching =
      floorMapState === FloorMapStates.ViewSpacesUSF
        ? MeasurementState.DISPLAY_SHAPES
        : MeasurementState.NONE;

    yield all([
      put(getProjectInfoById(currentProject.id)),
      put(getSpaces(currentFloor.id)),
      put(
        fetchShapesAction({
          measurementStateAfterFetching,
        })
      ),
    ]);
  } catch (e) {
    yield put(handleError(e));
  }
}

function* exitFloorplanOperation(
  action: ReturnType<typeof exitFloorplanOperationAction>
) {
  const bulkSelectionState: ReturnType<typeof selectBulkSelectionState> = yield select(
    selectBulkSelectionState
  );
  const shape: SpaceShapeVM = yield select(selectFloorUsfShapeToCopy);
  const { onConfirm } = action.payload;

  if (
    shouldOpenConfirmExitFloorplanOperationPrompt(bulkSelectionState, shape)
  ) {
    const resultAction: ReturnType<
      typeof exitFloorplanOperationConfirm | typeof exitFloorplanOperationCancel
    > = yield take([
      EXIT_FLOORPLAN_OPERATION_CONFIRM,
      EXIT_FLOORPLAN_OPERATION_CANCEL,
    ]);

    if (resultAction.type === EXIT_FLOORPLAN_OPERATION_CONFIRM && onConfirm) {
      onConfirm();
    }
  } else if (onConfirm) {
    onConfirm();
  }
}

function* clearSelectedSpace() {
  const floorMapState: ReturnType<typeof selectFloorMapState> = yield select(
    selectFloorMapState
  );
  if (
    ![FloorMapStates.AddSpaceType, FloorMapStates.EditSpaceType].includes(
      floorMapState
    )
  ) {
    yield put(setSelectedSpace(null));
  }
}

function* fetchSpacesWorksheetSpreadsheet(
  action: ReturnType<typeof fetchSpacesWorksheetSpreadsheetAction>
) {
  try {
    const { floorId } = action.payload;
    const clientFacingNamesAreVisible: boolean = yield select(
      selectClientFacingNamesAreVisible
    );
    const { data } = yield call(fetchSpacesWorksheetSpreadsheetApi, {
      floorId,
      clientFacingNamesAreVisible,
    });

    saveAs(
      `data:${XLSX_MIME_TYPE};base64,${data.spreadsheet.data}`,
      data.spreadsheet.name
    );

    yield put(fetchSpacesWorksheetSpreadsheetSuccess());
  } catch (e) {
    yield put(fetchSpacesWorksheetSpreadsheetFailed());
    yield put(handleError(e));
  }
}

function* fetchFloorplanPDFCall() {
  try {
    const floor: IFloor = yield select(selectCurrentFloor);
    const building: IBuilding = yield select(selectCurrentBuilding);
    const currentControlState: IFloorplanControlsState = yield select(
      selectCurrentControlState
    );

    const measurementState: MeasurementState = yield select(
      selectMeasurementState
    );

    const selectedCategories = {
      [INDIVIDUAL_WORKSPACES_CATEGORY]:
        currentControlState.showIndividualWorkSpaces,
      [MEETING_SPACES_CATEGORY]: currentControlState.showMeetingSpaces,
      [AMENITY_SPACES_CATEGORY]: currentControlState.showAmenitySpaces,
      [SUPPORT_SPACES_CATEGORY]: currentControlState.showSupportSpaces,
      [CUSTOM_CATEGORY]: currentControlState.showOtherSpaces,
    };

    const alternativeWorkpointsByCategory = {
      [INDIVIDUAL_WORKSPACES_CATEGORY]:
        currentControlState.showIndividualWorkspacesAltWorkpoints,
      [MEETING_SPACES_CATEGORY]:
        currentControlState.showMeetingSpacesAltWorkpoints,
      [AMENITY_SPACES_CATEGORY]:
        currentControlState.showAmenitySpacesAltWorkpoints,
      [SUPPORT_SPACES_CATEGORY]:
        currentControlState.showSupportSpacesAltWorkpoints,
    };

    const categories = Object.entries(selectedCategories)
      .filter(([, value]) => value)
      .map(([category]) => snakeCase(category));

    const categoriesWithVisibleAltWorkpoints = Object.entries(
      alternativeWorkpointsByCategory
    )
      .filter(([, value]) => value)
      .map(([category]) => snakeCase(category));

    const { data } = yield call(floorPlanApi.downloadFloorplanPDF, floor.id, {
      categories,
      categoriesWithVisibleAltWorkpoints,
      showWorkstations:
        currentControlState.showIndividualWorkSpaces &&
        !currentControlState.showIndividualWorkSpacesIntermediate,
      showSpaceUsf: measurementState === MeasurementState.DISPLAY_SHAPES,
      showFloorUsf: measurementState === MeasurementState.INSPECT_FLOOR_USF,
      seatCount: currentControlState.showSeatCountLayer,
      spaceTypes: currentControlState.showSpaceTypeLayer,
      photos: currentControlState.onlyShowPhotos,
      notes: currentControlState.onlyShowNotes,
    });

    const image = data.pdf;

    saveAs(
      `data:application/pdf;base64,${image}`,
      `${building.streetAddress}_${floor.name}.pdf`
    );

    yield put(fetchFloorplanPDFSuccess());
  } catch (e) {
    yield put(fetchFloorplanPDFFailed());
    yield put(handleError(e));
  }
}

function* setBulkOperationSourceFloorDataSaga(
  action: ReturnType<typeof setBulkSelectionState>
) {
  if (
    ![BulkSelectionState.COPY, BulkSelectionState.MOVE].includes(action.payload)
  )
    return;
  const currentFloor: IFloor = yield select(selectCurrentFloor);
  const calibration: ICalibration = yield select(selectCalibration);
  yield put(
    setBulkOperationSourceFloorData({
      floorName: currentFloor.name,
      calibration,
    })
  );
}

export default function* floorplanSagas() {
  yield all([
    takeLatest(FETCH_FLOORPLAN_GEOJSON, fetchFloorplanGeoJson),
    takeEvery(UPLOAD_FLOORPLAN, uploadFloorplan),
    takeEvery(ARCHIVE_FLOORPLAN, archiveFloorplanCall),
    takeEvery(SAVE_FLOOR_IMAGE, saveFloorImage),
    takeLatest(FETCH_FLOORPLAN, fetchFloorplan),
    takeLatest(FETCH_FLOORPLAN_UPLOAD, fetchFloorplanUploadCall),
    takeLatest(ADD_SPACE, addSpace),
    takeLatest(GET_SPACES, getFloorSpaces),
    takeEvery(GET_SPACE_TO_UPDATE, getSpaceToUpdate),
    takeEvery(UPDATE_SPACE, updateSpace),
    takeEvery(UPDATE_SPACE_BUBBLE_POSITION, updateSpaceBubblePositionCall),
    takeEvery(CREATE_SPACE_FROM_CLUSTER, createSpaceFromClusterCall),
    takeEvery(UPDATE_SPACE_POSITION, updateSpacePosition),
    takeEvery(SET_ADD_FLOOR_NOTE_STATE, setAddFloorNoteState),
    takeEvery(SEAT_COUNT_CLICKED, handleSeatCountState),
    takeEvery(SPACE_TYPE_CLICKED, handleSpaceTypeState),
    takeEvery(VIEW_SPACES_USF_CLICKED, handleViewSpacesUSFState),
    takeEvery(PHOTO_FILTER_CLICKED, handlePhotoState),
    takeEvery(NOTE_FILTER_CLICKED, handleNotesState),
    takeEvery(TOGGLE_INDIVIDUAL_SPACES, toggleIndividualStates),
    takeEvery(TOGGLE_MEETING_SPACES, toggleMeetingSpacesStates),
    takeEvery(TOGGLE_AMENITY_SPACES, toggleAmenitySpacesStates),
    takeEvery(TOGGLE_SUPPORT_SPACES, toggleSupportSpacesStates),
    takeEvery(ATTACH_NOTE, attachNote),
    takeEvery(ADD_UNATTACHED_NOTE, addUnattachedNote),
    takeEvery(DELETE_SPACE, deleteSpace),
    takeEvery(PUT_DISTANCE_CONSTANT, createFloorCalibration),
    takeEvery(FETCH_CALIBRATION, fetchCalibration),
    takeEvery(FETCH_SUGGESTED_CALIBRATION, fetchSuggestedCalibrationCall),
    takeEvery(UPDATE_SUGGESTED_CALIBRATION, updateSuggestedCalibrationCall),
    takeLeading(SAVE_ACTIVE_SHAPE, createShape),
    takeEvery(FETCH_FLOORPLAN_SHAPES, fetchShapes),
    takeLatest(UPDATE_ACTIVE_SHAPE, updateShape),
    takeEvery(DELETE_ACTIVE_SHAPE, deleteShape),
    takeLatest(DELETE_SHAPE, deleteShapeOnUpdateSpaceHook),
    takeEvery(COPY_MEASURED_FLOOR_USF, copyMeasuredFloorUsf),
    takeEvery(DELETE_NOTE, deleteNote),
    takeEvery(UPDATE_NOTE, updateNote),
    takeEvery(UPDATE_CLUSTER, updateCluster),
    takeLeading(CONFIRM_ADD_SPACE, confirmAddSpace),
    takeEvery(DELETE_CLUSTER, deleteCluster),
    takeEvery(CROP_FLOORPLAN_IMAGE, cropFloorplanImage),
    takeEvery(START_PDF_UPLOAD, startPDFUpload),
    takeLatest(DELETE_SELECTED_SPACES, deleteSelectedSpacesCall),
    takeEvery(CREATE_SPACES, createSpaces),
    takeLatest(UPDATE_MULTIPLE_SPACES, updateMultipleSpacesCall),
    takeEvery(EXIT_FLOORPLAN_OPERATION, exitFloorplanOperation),
    takeEvery(CLEAR_SELECTED_SPACE, clearSelectedSpace),
    takeLatest(
      FETCH_SPACES_WORKSHEET_SPREADSHEET,
      fetchSpacesWorksheetSpreadsheet
    ),
    takeLatest(FETCH_FLOORPLAN_PDF, fetchFloorplanPDFCall),
    takeLatest([SET_BULK_SELECTION_STATE], setBulkOperationSourceFloorDataSaga),
  ]);
}
